import { Callable, TimeoutId } from '@prophecy/interfaces/generic';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { ToastUtilReturnType } from '@prophecy/ui-v3/LegacyToast/types';

const PING_INTERVAL = 10 * 1000; // 10 sec
const BUFFER = 5 * 1000; // 5 sec

export const WS_TIMEOUT_INTERVAL = 3 * 60 * 1000; // 3 minutes

export class KeepAliveClient {
  private _retryToastTimeout?: TimeoutId;

  private _retryToast?: ToastUtilReturnType;

  private _showToast?: boolean;

  private handlePong: Callable;

  public preventDisconnect?: boolean;

  private closeConnection: () => void;

  private pingTimeoutInterval: number = PING_INTERVAL + BUFFER;

  constructor(options: { closeConnection: () => void; handlePong: () => void }) {
    this.closeConnection = options.closeConnection;
    this.handlePong = options.handlePong;

    this.handlePingTimeout();
  }

  private clearToast() {
    if (this._retryToast) {
      this._retryToast.close();
    }
    if (this._retryToastTimeout) clearTimeout(this._retryToastTimeout);
  }

  private handlePingTimeout() {
    /**
     * this is just for notifying purpose, we don't automatically retry anything as such.
     * This is to handle the case where backend is closed, but from the frontend perspective the
     * socket is still up. In this case ping will not come from backend.
     * The socket timeout after 1-2 min. so in between user will see this toast
     * So we proactively close the connection if we don't recieve a ping from the backend for on specific timeout (15s)
     */
    this._retryToastTimeout = setTimeout(() => {
      this.closeConnection();

      if (!document.hidden && this._showToast) {
        this._retryToast = toast.loading({
          content: 'Retrying to connect IDE.',
          placement: 'bottom',
          closeable: false
        });
      }
    }, this.pingTimeoutInterval);
  }

  private visibilityChange = () => {
    // we need to refresh token when ever there visibility change visible -> hidden or hidden to active.
    // visible -> hidden is required so that we make sure, token is not expired just after the tab change
    // hidden -> visible is required so that we can check if token is valid
    this.sendPong();
  };

  private sendPong() {
    this.handlePong();
  }

  public init(showToast = true) {
    this._showToast = showToast;
    document.addEventListener('visibilitychange', this.visibilityChange);
  }

  public setPingTimeoutInterval(delay: number) {
    this.pingTimeoutInterval = delay;
    // reset the toast
    if (!document.hidden || this.preventDisconnect) {
      this.handlePingTimeout();
    }
  }

  public onPing() {
    this.clearToast();

    this.pingTimeoutInterval = PING_INTERVAL + BUFFER;

    if (!document.hidden || this.preventDisconnect) {
      this.handlePingTimeout();
      this.sendPong();
    }
  }

  public onClose() {
    document.removeEventListener('visibilitychange', this.visibilityChange);

    this.clearToast();
  }
}
